import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  Collapse,
  Alert,
  Switch,
  FormControlLabel,
  Link
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AdminService from '../../services/AdminService';
import { format } from 'date-fns';

const QueryLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalLogs, setTotalLogs] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  
  // Filter states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [userId, setUserId] = useState('');
  const [model, setModel] = useState('');
  
  // Helper function to generate consistent row keys
  const getRowKey = (log, index) => {
    return log.id || `${page}-${index}`;
  };
  
  const fetchLogs = useCallback(async () => {
  try {
    setLoading(true);
    setError(null);

    const params = {
      page: page + 1, // API uses 1-based pagination
      limit: rowsPerPage,
    };

    // Add date filters if they exist
    if (startDate) {
      // Convert to MM/DD/YYYY format which matches the format in the CSV
      params.start_date = format(startDate, 'yyyy-MM-dd');
    }

    if (endDate) {
      // Convert to MM/DD/YYYY format which matches the format in the CSV
      params.end_date = format(endDate, 'MM/dd/yyyy');
    }

    // Add userId filter if it exists
    if (userId) {
      params.user_id = userId;
    }

    // Add model filter if it exists
    if (model) {
      params.model = model;
    }

    console.log("Fetching query logs with params:", params);

    const response = await AdminService.getQueryLogs(params);
    console.log("Query logs response:", response);

    if (response && response.logs) {
      console.log("Received query logs:", response.logs.length);
      setLogs(response.logs);
      setTotalLogs(response.total_count || response.total || response.logs.length);
      console.log("Total logs count:", response.total_count || response.total);
    } else {
      setLogs([]);
      setTotalLogs(0);
    }

    // Update last refreshed timestamp
    setLastRefreshed(new Date());

    // Clear expanded rows when new data is loaded
    setExpandedRows({});

  } catch (error) {
    console.error('Error fetching query logs:', error);
    setError('Failed to load query logs. Please try again.');
  } finally {
    setLoading(false);
  }
}, [page, rowsPerPage, startDate, endDate, userId, model]);

  // Effect to fetch logs when dependencies change
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  
  // Set up auto-refresh
  useEffect(() => {
    let intervalId;
    
    if (autoRefresh) {
      intervalId = setInterval(() => {
        // Only auto-refresh if we're on the first page and no filters are applied
        if (page === 0 && !startDate && !endDate && !userId && !model) {
          fetchLogs();
        }
      }, refreshInterval * 1000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefresh, refreshInterval, fetchLogs, page, startDate, endDate, userId, model]);
  
  const handleChangePage = (event, newPage) => {
    console.log("Changing page from", page, "to:", newPage);
    setPage(newPage);
    // Clear expanded rows when changing pages
    setExpandedRows({});
  };
  
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    console.log("Changing rows per page to:", newRowsPerPage);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // Clear expanded rows when changing page size
    setExpandedRows({});
  };
  
  const handleApplyFilters = () => {
    setPage(0); // Reset to first page
    setExpandedRows({}); // Clear expanded rows
  };
  
  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setUserId('');
    setModel('');
    setPage(0);
    setExpandedRows({});
  };
  
  const handleRefresh = () => {
    fetchLogs();
  };

  const handleExportLogs = async () => {
    try {
      setLoading(true);
      
      const params = {};
      
      // Add all current filters to the export
      if (startDate) {
        params.start_date = format(startDate, 'yyyy-MM-dd');
      }
      
      if (endDate) {
        params.end_date = format(endDate, 'yyyy-MM-dd');
      }
      
      if (userId) {
        params.user_id = userId;
      }
      
      if (model) {
        params.model = model;
      }
      
      // Try to use the export endpoint if it exists
      try {
        const response = await AdminService.exportQueryLogs(params);
        
        // If the API returns a download URL, use it
        if (response && response.downloadUrl) {
          const link = document.createElement("a");
          link.href = response.downloadUrl;
          link.download = `query_logs_${format(new Date(), 'yyyy-MM-dd')}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Revoke the object URL to free up memory
          window.URL.revokeObjectURL(response.downloadUrl);
          return;
        }
      } catch (apiError) {
        console.log('Export API not available, falling back to client-side export');
      }
      
      // Client-side fallback: Fetch ALL matching logs for export
      let allLogs = [];
      let currentPage = 1;
      let hasMoreData = true;
      const limitPerRequest = 100; // Fetch in batches of 100
      
      // Fetch all logs that match the current filters
      while (hasMoreData) {
        try {
          const exportParams = {
            ...params,
            page: currentPage,
            limit: limitPerRequest
          };
          
          const response = await AdminService.getQueryLogs(exportParams);
          
          if (response && response.logs && response.logs.length > 0) {
            allLogs = allLogs.concat(response.logs);
            
            // Check if we've fetched all available logs
            if (response.logs.length < limitPerRequest || allLogs.length >= (response.total || 0)) {
              hasMoreData = false;
            } else {
              currentPage++;
            }
          } else {
            hasMoreData = false;
          }
        } catch (fetchError) {
          console.error('Error fetching logs for export:', fetchError);
          
          // If API fails, fall back to using currently loaded logs
          allLogs = logs;
          hasMoreData = false;
        }
      }
      
      // If no logs were fetched, show an error
      if (allLogs.length === 0) {
        setError('No logs found to export with the current filters.');
        return;
      }
      
      // Helper function to format token usage for CSV
      const formatTokenUsageForCSV = (tokenUsage) => {
        if (!tokenUsage) return '';
        
        if (typeof tokenUsage === 'object') {
          // Format as "total: X, prompt: Y, completion: Z"
          const parts = [];
          if (tokenUsage.total) parts.push(`total: ${tokenUsage.total}`);
          if (tokenUsage.prompt) parts.push(`prompt: ${tokenUsage.prompt}`);
          if (tokenUsage.completion) parts.push(`completion: ${tokenUsage.completion}`);
          return parts.join(', ');
        }
        
        return String(tokenUsage);
      };
      
      // Helper function to format duration for CSV
      const formatDurationForCSV = (duration) => {
        if (!duration) return '0';
        
        // If duration is already a number (in milliseconds), convert to seconds
        if (typeof duration === 'number') {
          return duration < 1000 ? `${duration}ms` : `${(duration / 1000).toFixed(2)}s`;
        }
        
        // If duration is a string, return as-is
        return String(duration);
      };
      
      // Helper function to escape CSV fields
      const escapeCsvField = (field) => {
        if (field === null || field === undefined) return '';
        
        const stringField = String(field);
        
        // If field contains quotes, commas, or newlines, wrap in quotes and escape internal quotes
        if (stringField.includes('"') || stringField.includes(',') || stringField.includes('\n') || stringField.includes('\r')) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        
        return stringField;
      };
      
      // Create CSV content with proper headers
      const csvHeaders = [
        'Timestamp',
        'Session ID', 
        'User Prompt',
        'Response',
        'Duration',
      ];
      
      const csvRows = allLogs.map(log => {
        const tokenUsage = log.token_usage || {};
        
        return [
          escapeCsvField(log.timestamp),
          escapeCsvField(log.session_id),
          escapeCsvField(log.user_prompt),
          escapeCsvField(log.full_response),
          escapeCsvField(formatDurationForCSV(log.duration)),
        ];
      });
      
      // Combine headers and rows
      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.join(','))
      ].join('\n');
      
      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `query_logs_${format(new Date(), 'yyyy-MM-dd')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Revoke the object URL to free up memory
        URL.revokeObjectURL(url);
        
        console.log(`Successfully exported ${allLogs.length} logs to CSV`);
      }
      
    } catch (err) {
      console.error('Error exporting logs:', err);
      setError('Failed to export logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleRowExpansion = (rowKey) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowKey]: !prev[rowKey]
    }));
  };
  
  const formatDuration = (duration) => {
    if (!duration) return '0ms';
    if (duration < 1000) {
      return `${duration}ms`;
    } else {
      return `${(duration / 1000).toFixed(2)}s`;
    }
  };
  
  const handleToggleAutoRefresh = (event) => {
    setAutoRefresh(event.target.checked);
  };

  const handleViewChat = (sessionId) => {
    // This would typically navigate to a chat session view page
    console.log(`View chat session: ${sessionId}`);
    // navigate(`/admin/chat-sessions/${sessionId}`);
  };

  // Fixed handleAction function
  const handleAction = (log, index) => {
    const rowKey = getRowKey(log, index);
    toggleRowExpansion(rowKey);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Query Logs</Typography>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                    margin: 'dense'
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                    margin: 'dense'
                  },
                }}
              />
            </Grid>
          </LocalizationProvider>
          
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Session ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              variant="outlined"
              margin="dense"
            />
          </Grid>
                    
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleApplyFilters}
                sx={{ flexGrow: 1 }}
              >
                Apply
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleClearFilters}
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">
            Query Log Entries
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last refreshed: {lastRefreshed.toLocaleTimeString()}
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={handleToggleAutoRefresh}
                color="primary"
              />
            }
            label="Auto-refresh"
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportLogs}
          >
            Export CSV
          </Button>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="30px"></TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell>Session ID</TableCell>
                <TableCell>User Prompt</TableCell>
                <TableCell>Response</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No query logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, index) => {
                  const rowKey = getRowKey(log, index);
                  return (
                    <React.Fragment key={rowKey}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpansion(rowKey)}
                            aria-label="expand row"
                          >
                            {expandedRows[rowKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          {new Date(log.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>{log.session_id}</TableCell>
                        <TableCell>
                          <Tooltip title={log.user_prompt}>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 200,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {log.user_prompt}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={log.needs_expansion ? "Click to view full response" : log.response}>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 250,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                cursor: log.needs_expansion ? 'pointer' : 'default',
                                textDecoration: log.needs_expansion ? 'underline' : 'none',
                                color: log.needs_expansion ? 'primary.main' : 'inherit',
                              }}
                              onClick={log.needs_expansion ? () => toggleRowExpansion(rowKey) : undefined}
                            >
                              {log.response}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{formatDuration(log.duration)}</TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              onClick={() => handleAction(log, index)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                          <Collapse in={expandedRows[rowKey]} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 1 }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Session Details
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Session ID:</strong> {log.session_id}
                                  </Typography>
                                  {/* <Box sx={{ mt: 1 }}>
                                    <Link 
                                      component="button" 
                                      variant="body2"
                                      onClick={() => handleViewChat(log.session_id)}
                                    >
                                      View full conversation
                                    </Link>
                                  </Box> */}
                                </Grid>
                                
                                <Grid item xs={12}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    User Prompt
                                  </Typography>
                                  <Paper variant="outlined" sx={{ p: 1, maxHeight: 100, overflow: 'auto' }}>
                                    <Typography variant="body2">
                                      {log.user_prompt || 'No prompt recorded'}
                                    </Typography>
                                  </Paper>
                                </Grid>
                                
                                <Grid item xs={12}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Full Response
                                  </Typography>
                                  <Paper variant="outlined" sx={{ p: 1, maxHeight: 200, overflow: 'auto' }}>
                                    <Typography variant="body2">
                                      {log.full_response || log.response || 'No response recorded'}
                                    </Typography>
                                  </Paper>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalLogs}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default QueryLogs;